import { Badge, LinkOpenIcon } from '@thefarmersfront/kitchen';

import type { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';

import { TextButtonCell } from '~/components/common';

import { formatDate } from '~/utils/date';

import {
  DATE_FORMAT,
  DELIVERY_AGENCY_COURIER_CODE,
  DELIVERY_AGENCY_SHIPPING_LABEL_STATUS,
  DELIVERY_AGENCY_SHIPPING_LABEL_STATUS_BADGE_COLOR,
  DELIVERY_AGENCY_TRACKING_LINKS,
} from '~/constants';

import type { TypeDeliveryAgencyShippingLabelDetail } from '~/types/delivery-agency.types';

import styles from './getDeliveryAgencyTrackingShippingLabelInfoColumnDefs.module.scss';

type TypeShippingLabelInfo = TypeDeliveryAgencyShippingLabelDetail['shippingLabelInfo'];

const getDeliveryAgencyTrackingShippingLabelInfoColumnDefs = () => {
  const columnDefs: ColDef<TypeShippingLabelInfo>[] = [
    {
      field: 'shippingLabelNumber',
      headerName: '운송장 번호',
      width: 245,
    },
    {
      field: 'status',
      headerName: '운송장 상태',
      cellDataType: false,
      width: 262,
      cellRenderer: ({ value, data }: ICellRendererParams<TypeShippingLabelInfo, TypeShippingLabelInfo['status']>) => {
        if (!value) return null;

        const showCancelReason =
          value.code === DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.DELIVERY_CANCELED.value &&
          data?.deliveryCancelReason?.code === 'OUTBOUND_RESET';

        return (
          <div className={styles['status-cell']}>
            <Badge variant="light" status color={DELIVERY_AGENCY_SHIPPING_LABEL_STATUS_BADGE_COLOR[value.code]}>
              {value.description}
            </Badge>
            {showCancelReason && <span>({data.deliveryCancelReason?.description})</span>}
          </div>
        );
      },
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      field: 'statusUpdatedAt',
      headerName: '상태 마지막 업데이트 일시',
      width: 170,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<TypeShippingLabelInfo, TypeShippingLabelInfo['statusUpdatedAt']>) =>
        formatDate(value, DATE_FORMAT.DATE_TIME) ?? '-',
    },
    {
      field: 'deliveryType.description',
      headerName: '배송 유형',
      width: 80,
    },
    {
      field: 'courierCode.description',
      headerName: '배송사',
      width: 88,
    },
    {
      field: 'shippingLabelNumber',
      headerName: '',
      width: 150,
      cellRenderer: TextButtonCell,
      cellRendererParams: ({
        data,
      }: ICellRendererParams<TypeShippingLabelInfo, TypeShippingLabelInfo['shippingLabelNumber']>) => {
        const courierCode = data?.courierCode.code as keyof typeof DELIVERY_AGENCY_COURIER_CODE | undefined;
        const invoiceNumber = data?.shippingLabelNumber;
        const url = courierCode && invoiceNumber ? DELIVERY_AGENCY_TRACKING_LINKS[courierCode] + invoiceNumber : '';

        return {
          children: '배송조회',
          size: 'tiny',
          variant: 'normal',
          target: '_blank',
          href: url,
          rightIcon: <LinkOpenIcon size={16} color="#672091" />,
        };
      },
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
  ];

  return { columnDefs };
};

export default getDeliveryAgencyTrackingShippingLabelInfoColumnDefs;
